/**
 * Accords de devis — et devis complémentaires restés sans réponse après le dernier
 * rappel — en haut à droite, jusqu'à ce que quelqu'un clique « Traité ».
 *
 * L'état « traité » vit côté serveur sur la notification d'org : il survit au
 * rechargement et il est partagé — un accord traité par Lana disparaît aussi chez
 * Titouan (au prochain rafraîchissement : arrivée d'une notification ou retour sur
 * l'onglet). Plus de sondage toutes les 60 s : le flux des notifications in-app
 * signale déjà chaque nouvel accord, et le sondage coûtait ~8 000 appels par jour.
 * Sous les modales (10000) pour ne pas masquer une fiche ouverte.
 *
 * Le serveur ne renvoie que les accords destinés à l'utilisateur (l'org peut réserver
 * ceux du devis initial et ceux des complémentaires à certains membres). La pile se
 * réduit en une pastille, comme le bandeau de mise à jour, pour finir un dossier et
 * traiter l'accord ensuite ; un nouvel accord ne la rouvre pas, la pastille le compte.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import Icon1 from "@assets/icons/Icon";
import { colors } from "sexy";
import { notificationsApi, type DevisAccord } from "@services/api/notifications";
import { useInAppNotificationsStore } from "@store/inAppNotificationsStore";
import { navigateTo } from "context/navigation.service";

// Retour sur l'onglet : pas plus d'un rafraîchissement par minute.
const VISIBLE_REFRESH_MIN_MS = 60_000;
const MAX_VISIBLE = 4;
const MINIMIZED_KEY = "devisAccordsMinimized";
// Un nouvel accord arrivé pendant que la pile est réduite fait sauter la pastille.
const NEW_ACCORD_BOUNCE_MS = 4_000;

function readMinimized(): boolean {
  try {
    return localStorage.getItem(MINIMIZED_KEY) === "1";
  } catch {
    return false;
  }
}

const INTERNET_RDV_COLOR = "#DC2626";

function formatReception(seconds?: number | null): string {
  if (!seconds) return "";
  const d = new Date(seconds * 1000);
  return `${d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
}

function accordTitle(a: DevisAccord): string {
  if (a.type === "low_rating") return `Avis négatif ${a.rating ?? "?"}/5 — recontacter le client`;
  if (a.type === "internet_rdv") return "RDV pris sur le site constructeur";
  if (a.no_response && a.devis_kind === "initial") {
    if (a.stage === "last_reminder_sent") {
      const rank = a.reminder_rank ?? 2;
      const label = rank <= 1 ? "1re relance" : `${rank}e relance`;
      const sent = a.delivered === false ? "non délivrée" : "envoyée";
      return a.cancel_time
        ? `${label} ${sent} — sans réponse, RDV annulé à ${a.cancel_time}`
        : `${label} ${sent} — sans réponse, le RDV risque d'être annulé`;
    }
    return a.cancel_time
      ? `RDV annulé à ${a.cancel_time} — joindre le client`
      : "1er devis sans réponse — joindre le client";
  }
  if (a.no_response) return "Devis complémentaire sans réponse — à reprendre";
  if (a.is_modification) return "Devis modifié accepté";
  if (a.devis_kind === "initial") return "Devis initial accepté";
  if (a.devis_kind === "complementaire") return "Devis complémentaire accepté";
  return "Devis accepté";
}

// Un 1er devis sur le point d'être annulé passe en rouge : c'est l'alerte prioritaire.
function accent(a: DevisAccord): string {
  if (a.type === "low_rating") return colors.red_500;
  if (a.type === "internet_rdv") return INTERNET_RDV_COLOR;
  if (a.no_response && a.devis_kind === "initial") return colors.red_500;
  return a.no_response ? colors.orange_500 : colors.emerald_500;
}

function formatAmount(amount: number | null): string {
  if (amount == null) return "";
  return amount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return sameDay ? `aujourd'hui ${time}` : `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })} ${time}`;
}

export default function DevisAccordsPopup() {
  const [accords, setAccords] = useState<DevisAccord[]>([]);
  const [treating, setTreating] = useState<Set<string>>(new Set());
  const [minimized, setMinimizedState] = useState(readMinimized);
  const [bounce, setBounce] = useState(false);
  // Une nouvelle notification in-app (devis signé ou autre) déclenche un rafraîchissement.
  const latestNotifId = useInAppNotificationsStore((s) => s.notifications[0]?.id);

  const lastRefresh = useRef(0);
  // Ids déjà reçus — null avant le premier chargement, qui ne compte pas comme « nouveau ».
  const loaded = useRef(false);
  const seenIds = useRef<Set<string> | null>(null);
  const minimizedRef = useRef(minimized);
  minimizedRef.current = minimized;

  const setMinimized = (value: boolean) => {
    setMinimizedState(value);
    try {
      localStorage.setItem(MINIMIZED_KEY, value ? "1" : "0");
    } catch {
      // stockage indisponible (navigation privée…) : l'état ne vaut que pour la session
    }
  };

  useEffect(() => {
    if (!loaded.current) return;
    const prev = seenIds.current;
    seenIds.current = new Set(accords.map((a) => a.id));
    if (!minimizedRef.current) return;
    // Tout est traité : le prochain accord s'affichera en grand.
    if (accords.length === 0) setMinimized(false);
    else if (prev && accords.some((a) => !prev.has(a.id))) setBounce(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accords]);

  useEffect(() => {
    if (!bounce) return;
    const t = setTimeout(() => setBounce(false), NEW_ACCORD_BOUNCE_MS);
    return () => clearTimeout(t);
  }, [bounce]);

  const refresh = useCallback(() => {
    lastRefresh.current = Date.now();
    notificationsApi
      .listDevisAccords()
      .then((list) => {
        loaded.current = true;
        setAccords(list);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastRefresh.current >= VISIBLE_REFRESH_MIN_MS) {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  useEffect(() => {
    if (latestNotifId) refresh();
  }, [latestNotifId, refresh]);

  const markTreated = (id: string) => {
    setTreating((s) => new Set(s).add(id));
    notificationsApi
      .markDevisAccordTreated(id)
      .then(() => setAccords((list) => list.filter((a) => a.id !== id)))
      .catch(() => {})
      .finally(() =>
        setTreating((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        }),
      );
  };

  if (accords.length === 0) return null;
  const visible = accords.slice(0, MAX_VISIBLE);
  const hidden = accords.length - visible.length;

  if (minimized) {
    const dot = accords.some((a) => a.type === "internet_rdv" || a.type === "low_rating") ? "bg-red-500"
      : accords.some((a) => a.no_response) ? "bg-orange-400" : "bg-emerald-400";
    return (
      <div
        onClick={() => setMinimized(false)}
        title="Afficher les accords de devis"
        className={`fixed top-3 right-3 z-[9000] flex items-center gap-2 cursor-pointer rounded-full bg-neutral-900 py-2 pl-4 pr-2 shadow-lg text-white hover:translate-y-0.5 transition-all duration-300 ${bounce ? "animate-bounce" : ""}`}
      >
        <span className="relative flex h-2 w-2">
          <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${dot}`} />
          <span className={`relative inline-flex h-2 w-2 rounded-full ${dot}`} />
        </span>
        <span className="text-h3 font-medium">
          {accords.length} à traiter
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setMinimized(false); }}
          aria-label="Agrandir"
          className="flex h-6 w-6 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-700 hover:text-white transition-colors"
        >
          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed top-3 right-3 z-[9000] flex flex-col items-end gap-2 w-[320px]">
      {/* Réduire : finir le dossier en cours et traiter l'accord plus tard. */}
      <button
        onClick={(e) => { e.stopPropagation(); setMinimized(true); }}
        aria-label="Réduire — traiter plus tard"
        title="Réduire — traiter plus tard"
        className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1 shadow border-1 border-shadow_10 text-h4 font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 transition-colors"
      >
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6" /></svg>
        Réduire
      </button>
      {visible.map((a) => (
        <div
          key={a.id}
          className="w-full bg-white rounded-2xl shadow-lg border-1 border-shadow_10 overflow-hidden"
          style={{ borderLeft: `4px solid ${accent(a)}` }}
        >
          <div
            className="flex flex-row items-start gap-3 px-4 pt-3 pb-2 cursor-pointer hover:bg-neutral-50"
            onClick={() => a.ordre_id && navigateTo({ route: `/ordre/${a.ordre_id}` })}
          >
            <div
              className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
              style={{ backgroundColor: `${accent(a)}18` }}
            >
              <Icon1 name={a.type === "low_rating" ? "star" : "devis"} size={16} color={accent(a)} />
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-h3 font-semibold text-neutral-900 truncate">
                {accordTitle(a)}
                {a.amount != null && (
                  <span className="font-normal text-neutral-500"> · {formatAmount(a.amount)}</span>
                )}
              </span>
              <span className="text-h4 text-neutral-700 truncate">
                {a.client_name}
                {a.ordre_num ? ` — ${a.ordre_num}` : ""}
              </span>
              {a.vehicle && <span className="text-h4 text-neutral-500 truncate">{a.vehicle}</span>}
              {a.type === "low_rating" ? (
                <span className="text-h4 font-medium" style={{ color: accent(a) }}>
                  {"★".repeat(a.rating ?? 0)}{"☆".repeat(5 - (a.rating ?? 0))} · {formatWhen(a.created_at)} · appeler le client
                </span>
              ) : a.type === "internet_rdv" ? (
                <span className="text-h4 font-medium" style={{ color: INTERNET_RDV_COLOR }}>
                  Réception {formatReception(a.reception_seconds)} · à compléter dans ServiceBox :
                  restitution + équipe, puis « Valider »
                </span>
              ) : (
                <span className="text-h5 text-neutral-400">{formatWhen(a.created_at)}</span>
              )}
            </div>
          </div>
          <div className="flex flex-row justify-end px-3 pb-2">
            {a.type === "internet_rdv" ? (
              <button
                onClick={() => navigateTo({ route: `/ordre/${a.ordre_id}` })}
                className="px-3 py-1 rounded-lg text-h4 font-medium text-white"
                style={{ backgroundColor: accent(a) }}
              >
                Ouvrir
              </button>
            ) : (
            <button
              onClick={() => markTreated(a.id)}
              disabled={treating.has(a.id)}
              className="px-3 py-1 rounded-lg text-h4 font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: accent(a) }}
            >
              {treating.has(a.id) ? "…" : "Traité"}
            </button>
            )}
          </div>
        </div>
      ))}
      {hidden > 0 && (
        <div className="px-3 py-1 rounded-full bg-white shadow border-1 border-shadow_10 text-h4 text-neutral-600">
          + {hidden} autre{hidden > 1 ? "s" : ""} à traiter
        </div>
      )}
    </div>
  );
}
